import React from "react";
import { AlertTriangle, Anchor, CalendarArrowDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { BELEGG_TYPE, dm, relDays } from "@/lib/kapasitet";

function Col({ icon: Icon, title, children, testId }) {
  return (
    <section className="min-w-0" data-testid={testId}>
      <h3 className="mb-2 flex items-center gap-2 text-[13px] font-semibold text-foreground/85"><Icon className="h-4 w-4 text-muted-foreground" strokeWidth={2} /> {title}</h3>
      {children}
    </section>
  );
}

const Row = ({ onClick, children, testId }) => (
  <button type="button" onClick={onClick} className="flex w-full items-center gap-3 rounded-[10px] px-2 py-2 text-left text-[13px] outline-none transition-colors hover:bg-black/[0.03] focus-visible:ring-2 focus-visible:ring-ring/40" data-testid={testId}>{children}</button>
);
const Empty = ({ children, testId }) => <p className="px-2 py-2 text-[12.5px] text-muted-foreground" data-testid={testId}>{children}</p>;

/** Under the facilities' Gantt: who is in now, who arrives in the next three weeks, and the overlaps that have to be resolved. */
export function AnleggLister({ puls, rs, onBand, testId = "plan-anlegg" }) {
  if (!puls) return null;
  const find = (rid, key) => { const r = rs?.ressurser.find((x) => x.id === rid); const b = r?.bands.find((x) => x.key === key); return r && b ? [r, b] : null; };
  const open = (rid, key) => { const hit = find(rid, key); if (hit) onBand(hit[0], hit[1]); };
  const naa = puls.anlegg.filter((a) => a.naa);
  return (
    <div className="grid gap-8 md:grid-cols-3" data-testid={`${testId}-lister`}>
      <Col icon={Anchor} title="I dokk, på slipp og ved kai nå" testId={`${testId}-naa`}>
        {naa.length ? naa.map((a) => (
          <Row key={a.id} onClick={() => open(a.id, a.naa.key)} testId={`${testId}-naa-${a.id}`}>
            <span className="h-2.5 w-2.5 shrink-0 rounded-[3px] bg-[hsl(var(--navy))]" />
            <span className="min-w-0 flex-1"><span className="block truncate font-medium">{a.naa.navn}</span><span className="block truncate text-[11.5px] text-muted-foreground">{a.name} · {BELEGG_TYPE[a.naa.type]?.toLowerCase()} · ut {dm(a.naa.til)}</span></span>
            <span className="shrink-0 text-[12px] tabular-nums text-muted-foreground">{relDays(a.naa.til).replace("om ", "")} igjen</span>
          </Row>
        )) : <Empty testId={`${testId}-naa-empty`}>Alle anlegg er ledige akkurat nå.</Empty>}
      </Col>
      <Col icon={CalendarArrowDown} title="Kommende innflyttinger · 3 uker" testId={`${testId}-inn`}>
        {puls.innflyttinger.length ? puls.innflyttinger.slice(0, 6).map((b) => (
          <Row key={b.key} onClick={() => open(b.ressurs.id, b.key)} testId={`${testId}-inn-${b.key}`}>
            <span className={cn("h-2.5 w-2.5 shrink-0 rounded-[3px]", b.type === "reservasjon" ? "border border-dashed border-[hsl(var(--navy)/0.7)] bg-white" : "bg-[hsl(var(--navy))]")} />
            <span className="min-w-0 flex-1"><span className="block truncate font-medium">{b.navn}</span><span className="block truncate text-[11.5px] text-muted-foreground">{b.ressurs.name}{b.kunde ? ` · ${b.kunde}` : ""}{b.type === "reservasjon" ? " · reservasjon" : ""}</span></span>
            <span className="shrink-0 text-right text-[12px] tabular-nums text-muted-foreground"><span className="block font-medium text-foreground">{dm(b.fra)}</span>{relDays(b.fra)}</span>
          </Row>
        )) : <Empty testId={`${testId}-inn-empty`}>Ingen innflyttinger de neste tre ukene.</Empty>}
      </Col>
      <Col icon={AlertTriangle} title="Konflikter" testId={`${testId}-konflikter`}>
        {puls.konflikter.length ? puls.konflikter.map((k, i) => (
          <Row key={i} onClick={() => open(k.ressurs.id, k.a.key)} testId={`${testId}-konflikt-${i}`}>
            <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-[hsl(var(--v-blocked))]" />
            <span className="min-w-0 flex-1"><span className="block truncate font-medium">{k.ressurs.name}: {dm(k.fra)}–{dm(k.til)}</span><span className="block truncate text-[11.5px] text-muted-foreground">{k.a.navn} og {k.b.navn} · {k.dager} d{k.klargjoring ? " klargjøring" : ""}</span></span>
          </Row>
        )) : <Empty testId={`${testId}-konflikter-empty`}>Ingen overlapp på anleggene i perioden.</Empty>}
      </Col>
    </div>
  );
}
